import { jwtDecode } from "jwt-decode";
import { ReactNode, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { setSentryUser } from "@/v1/sentry";
import { createSession } from "./api/createSession";
import { logoutSession } from "./api/logoutSession";
import { AuthContext } from "./AuthContext";
import { ACCESS_TOKEN_REFRESH_LEAD_MS } from "./constants";
import { sessionEvents } from "./sessionEvents";
import { getOrStartRefresh } from "./sessionRefresh";
import { tokenStorage, type Tokens } from "./tokenStorage";
import type { Operator, PermissionScope } from "./types";
import { useGoogleOneTap } from "./useGoogleOneTap";
import { useMicrosoftLogin } from "./useMicrosoftLogin";
import type { Firm } from "../firms";

interface AuthProviderProps {
  children: ReactNode;
}

interface AccessTokenClaims {
  sub: string; // person id, not the operator account id (see operatorId)
  email: string;
  name: string;
  scope: PermissionScope;
  exp: number;
  operatorId: string;
  firm?: Firm;
}

const operatorFromAccessToken = (accessToken: string): Operator => {
  const claims = jwtDecode<AccessTokenClaims>(accessToken);
  return {
    id: claims.sub,
    operatorId: claims.operatorId,
    email: claims.email,
    name: claims.name,
    permissionScope: claims.scope,
    firm: claims.firm ?? "smarta",
    exp: claims.exp,
  };
};

/**
 * AuthProvider — manages JWT-based session tokens issued by smarta-accounts
 * through the /api/kontax/accounts/* gateway. On mount, replays the stored
 * session if the access token is still valid, or silently refreshes it.
 * A timer fires a silent refresh ~3 min before access-token expiry, and a
 * visibility-change listener refreshes when the tab becomes active again
 * inside the lead window. Refresh-token rejection bubbles up via
 * sessionEvents → the operator is logged out and sent to /login, where a
 * notice explains the session expired.
 */
export const AuthProvider = ({ children }: AuthProviderProps) => {
  const navigate = useNavigate();
  const { promptOneTap, credentialResponse } = useGoogleOneTap();
  const {
    clearError: clearMicrosoftLoginError,
    loginWithMicrosoft,
    idToken: microsoftIdToken,
    error: microsoftLoginError,
  } = useMicrosoftLogin();

  const [operator, setOperator] = useState<Operator | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const refreshTimerRef = useRef<number | null>(null);

  const clearRefreshTimer = () => {
    if (refreshTimerRef.current !== null) {
      window.clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  };

  const scheduleSilentRefresh = (accessTokenExpiresAt: number) => {
    clearRefreshTimer();
    const delay = Math.max(
      0,
      accessTokenExpiresAt - Date.now() - ACCESS_TOKEN_REFRESH_LEAD_MS
    );
    refreshTimerRef.current = window.setTimeout(() => {
      getOrStartRefresh()
        .then((tokens) => applyTokens(tokens))
        .catch(() => {
          // session:expired event already emitted by the singleton.
        });
    }, delay);
  };

  const applyTokens = (tokens: Tokens) => {
    const next = operatorFromAccessToken(tokens.accessToken);
    setOperator(next);
    setSentryUser({ id: next.id, email: next.email, name: next.name });
    scheduleSilentRefresh(tokens.accessTokenExpiresAt);
  };

  // Bootstrap from storage on mount.
  useEffect(() => {
    const bootstrap = async () => {
      const accessToken = tokenStorage.getAccessToken();
      const accessExpiresAt = tokenStorage.getAccessExpiresAt();
      const refreshToken = tokenStorage.getRefreshToken();

      if (!accessToken || !refreshToken || accessExpiresAt === null) {
        // A partial session (e.g. one token written but not the others) is
        // unusable — wipe it so no stale token lingers in storage.
        tokenStorage.clearTokens();
        setLoading(false);
        return;
      }

      try {
        if (accessExpiresAt > Date.now() + ACCESS_TOKEN_REFRESH_LEAD_MS) {
          // Access token still has runway — use it.
          const next = operatorFromAccessToken(accessToken);
          setOperator(next);
          setSentryUser({ id: next.id, email: next.email, name: next.name });
          scheduleSilentRefresh(accessExpiresAt);
        } else {
          // Inside the refresh window (or expired) — refresh first.
          const tokens = await getOrStartRefresh();
          applyTokens(tokens);
        }
      } catch {
        tokenStorage.clearTokens();
        setOperator(null);
        setSentryUser(null);
      } finally {
        setLoading(false);
      }
    };

    bootstrap();

    const handleVisibility = () => {
      if (document.visibilityState !== "visible") return;
      const exp = tokenStorage.getAccessExpiresAt();
      if (exp !== null && exp - Date.now() <= ACCESS_TOKEN_REFRESH_LEAD_MS) {
        getOrStartRefresh()
          .then((tokens) => applyTokens(tokens))
          .catch(() => {
            // session:expired already emitted on hard-fail
          });
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);

    const unsubscribeExpired = sessionEvents.onExpired(() => {
      // Refresh token rejected — the session is gone. Tear down locally and
      // bounce to /login with a flag so the page can explain why (no modal).
      tokenStorage.clearTokens();
      setOperator(null);
      setSentryUser(null);
      clearRefreshTimer();
      sessionStorage.setItem("auth:sessionExpired", "true");
      navigate("/login");
    });

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      unsubscribeExpired();
      clearRefreshTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // React to a Google sign-in credential by exchanging it for a session.
  useEffect(() => {
    if (!credentialResponse) return;

    const exchange = async () => {
      setLoading(true);
      setError(null);
      try {
        const tokens = await createSession({
          provider: "google",
          idToken: credentialResponse.credential,
        });
        tokenStorage.setTokens(tokens);
        sessionStorage.removeItem("auth:sessionExpired");
        applyTokens(tokens);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Login failed");
        tokenStorage.clearTokens();
      } finally {
        setLoading(false);
      }
    };

    exchange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [credentialResponse]);

  // React to a Microsoft ID token by exchanging it for the same session type.
  useEffect(() => {
    if (!microsoftIdToken) return;

    const exchange = async () => {
      setLoading(true);
      setError(null);
      try {
        const tokens = await createSession({
          provider: "microsoft",
          idToken: microsoftIdToken,
        });
        tokenStorage.setTokens(tokens);
        sessionStorage.removeItem("auth:sessionExpired");
        applyTokens(tokens);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Login failed");
        tokenStorage.clearTokens();
      } finally {
        setLoading(false);
      }
    };

    exchange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [microsoftIdToken]);

  const loginWithGoogle = () => {
    setError(null);
    clearMicrosoftLoginError();
    promptOneTap();
  };

  const startMicrosoftLogin = () => {
    setError(null);
    loginWithMicrosoft();
  };

  const logout = () => {
    const refreshToken = tokenStorage.getRefreshToken();
    const accessToken = tokenStorage.getAccessToken();
    if (refreshToken && accessToken) {
      // Best-effort server-side revocation: local logout still completes even
      // if it fails, so swallow the rejection to avoid an unhandled promise.
      void logoutSession({ refreshToken, accessToken }).catch(() => {});
    }
    tokenStorage.clearTokens();
    setOperator(null);
    setSentryUser(null);
    setError(null);
    clearRefreshTimer();
    sessionStorage.setItem("auth:logoutIntent", "true");
    navigate("/login");
  };

  return (
    <AuthContext.Provider
      value={{
        loginWithGoogle,
        loginWithMicrosoft: startMicrosoftLogin,
        operator,
        loading,
        error: error ?? microsoftLoginError,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
